import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Paper,
  Divider,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import InfoIcon from "@mui/icons-material/Info";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";

/**
 * HelpTooltip Component
 * Displays a stylish "?" help icon.
 * On click or tap, opens a clear popover explaining:
 * 1. What the option is for
 * 2. How it works
 * 3. What effect it will have in the mobile app
 */
const HelpTooltip = ({
  title = "Option Help",
  purpose = "",
  howItWorks = "",
  mobileEffect = "",
  placement = "bottom",
  size = "small",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size={size}
        onClick={handleClick}
        aria-label={`Help: ${title}`}
        sx={{
          ml: 0.8,
          p: 0.4,
          color: "rgba(76, 161, 175, 0.9)",
          bgcolor: "rgba(76, 161, 175, 0.12)",
          border: "1px solid rgba(76, 161, 175, 0.35)",
          borderRadius: "50%",
          width: size === "small" ? 22 : 26,
          height: size === "small" ? 22 : 26,
          transition: "all 0.2s ease",
          "&:hover": {
            bgcolor: "rgba(76, 161, 175, 0.28)",
            borderColor: "#4CA1AF",
            color: "#ffffff",
            transform: "scale(1.08)",
          },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: size === "small" ? 14 : 16 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: placement === "top" ? "top" : "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: placement === "top" ? "bottom" : "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 340,
            background: "linear-gradient(135deg, #13222f 0%, #1a2c3a 100%)",
            color: "#FFFFFF",
            borderRadius: 2.5,
            border: "1px solid rgba(76, 161, 175, 0.35)",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(16px)",
          },
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={1.2}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "rgba(76, 161, 175, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4CA1AF",
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#64d2e2" }}>
            {title}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mb: 1.5 }} />

        {/* What it is for */}
        {purpose && (
          <Box mb={1.2}>
            <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
              <InfoIcon sx={{ fontSize: 14, color: "#4CA1AF" }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255, 255, 255, 0.8)", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                What this is for
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.82rem", pl: 2.6, lineHeight: 1.4 }}>
              {purpose}
            </Typography>
          </Box>
        )}

        {/* How it works */}
        {howItWorks && (
          <Box mb={1.2}>
            <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
              <SettingsSuggestIcon sx={{ fontSize: 14, color: "#F59E0B" }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255, 255, 255, 0.8)", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                How it works
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.82rem", pl: 2.6, lineHeight: 1.4 }}>
              {howItWorks}
            </Typography>
          </Box>
        )}

        {/* Mobile App Effect */}
        {mobileEffect && (
          <Box
            sx={{
              p: 1.2,
              borderRadius: 1.5,
              bgcolor: "rgba(16, 185, 129, 0.12)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              mt: 1,
            }}
          >
            <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
              <SmartphoneIcon sx={{ fontSize: 14, color: "#10B981" }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#34D399", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                Mobile App Effect
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.92)", fontSize: "0.8rem", pl: 2.6, lineHeight: 1.35 }}>
              {mobileEffect}
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default HelpTooltip;
